import { yupResolver } from '@hookform/resolvers/yup'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton } from '@mui/lab'
import {
  Autocomplete,
  Box,
  Button,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'
import CancelIcon from '@mui/icons-material/Cancel'
import { useAppSelector } from 'app/hooks'
import postal_code from 'japan-postal-code'
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox'

type Props = {
  apiEndpoint?: string
  label: string
  prefectureId: number
  address: string
}
interface FormInput {
  prefectureId: number
  address: string
}

export default function EditAddress({
  apiEndpoint,
  prefectureId,
  address,
  label,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const schema = yup.object({
    prefectureId: yup.number().required(),
    address: yup.string().required(),
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      prefectureId,
      address,
    },
  })
  const UIPrefectures = useAppSelector((s) => s.UI.prefectures)
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (ref.current) {
      if (edit) {
        ref.current.querySelector('input')?.focus()
        ref.current.querySelector('input')?.focus()
      } else {
        ref.current.querySelector('input')?.blur()
        ref.current.querySelector('input')?.blur()
      }
    }
  }, [edit])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (apiEndpoint) {
      setLoading(true)
      try {
        await axiosClient.put(apiEndpoint, {
          data: JSON.stringify(data),
        })
        setEdit(false)
        const msg = 'イベントの' + label + 'を修正しました。'
        enqueueSnackbar(msg, {
          variant: 'success',
        })
        setLoading(false)
      } catch (_error) {
        const msg = 'エラーが発生しました。'
        enqueueSnackbar(msg, {
          variant: 'error',
        })
      }
    }
  }
  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      my={2}
    >
      {edit ? (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          alignItems="center"
          sx={{ display: edit ? 'flex' : 'none' }}
          ref={ref}
          spacing={2}
        >
          <Stack flex={1} sx={{ width: '100%' }}>
            <TextField
              margin="dense"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MarkunreadMailboxIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              label="郵便番号"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setEdit(false)
                  setValue('address', address, {
                    shouldValidate: true,
                  })
                  setValue('prefectureId', prefectureId, {
                    shouldValidate: true,
                  })
                }
              }}
              onChange={(e) => {
                const value = e.target.value
                if (value.length === 7) {
                  postal_code.get(value, (address) => {
                    const { area, city, prefecture, street } = address
                    const pre = UIPrefectures.find((f) => f.name === prefecture)
                    if (pre) {
                      setValue('prefectureId', pre.id, {
                        shouldValidate: true,
                      })
                    }
                    setValue('address', city + area + street, {
                      shouldValidate: true,
                    })
                  })
                }
              }}
              variant="standard"
            />
            <Autocomplete
              disablePortal
              options={UIPrefectures.map((v) => v.id)}
              getOptionLabel={(id) =>
                UIPrefectures.find((f) => f.id === id)?.name || ''
              }
              fullWidth
              value={getValues('prefectureId') || 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={'都道府県'}
                  required
                  variant="standard"
                  error={'prefectureId' in errors}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setEdit(false)
                      setValue('address', address, {
                        shouldValidate: true,
                      })
                      setValue('prefectureId', prefectureId, {
                        shouldValidate: true,
                      })
                    }
                  }}
                />
              )}
              onChange={(_event, newValue) => {
                newValue &&
                  setValue('prefectureId', newValue, {
                    shouldValidate: true,
                  })
              }}
            />
            <TextField
              variant={'standard'}
              required
              margin="dense"
              fullWidth
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setEdit(false)
                  setValue('address', address, {
                    shouldValidate: true,
                  })
                  setValue('prefectureId', prefectureId, {
                    shouldValidate: true,
                  })
                }
              }}
              label={label}
              {...register('address')}
              error={'address' in errors}
              helperText={errors.address?.message}
            />
          </Stack>
          <Stack
            spacing={4}
            direction={{ md: 'column', xs: 'row-reverse' }}
            flex={'none'}
          >
            <Button
              sx={{
                px: 1,
                minWidth: 0,
              }}
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setEdit(false)
                setValue('address', address, {
                  shouldValidate: true,
                })
                setValue('prefectureId', prefectureId, {
                  shouldValidate: true,
                })
              }}
            >
              <CancelIcon />
            </Button>
            <LoadingButton
              disabled={Object.keys(errors).length > 0}
              loading={loading}
              sx={{
                px: 1,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              type="submit"
            >
              <SaveAsIcon />
            </LoadingButton>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          spacing={2}
          sx={{ display: !edit ? 'flex' : 'none' }}
          alignItems={{
            md: 'center',
            xs: 'flex-start',
          }}
        >
          <Typography fontWeight="bold" variant="body2" flex={'none'}>
            {label}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography>
              {UIPrefectures.find((f) => f.id === prefectureId)?.name +
                address || '未設定'}
            </Typography>
            <Button
              sx={{
                flex: 'none',
                mx: 1,
                p: 0.5,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              color="info"
              onClick={() => {
                setEdit(true)
              }}
            >
              <ModeEditIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
